import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import config from '../config.js';
import { apiErrorMessage, detailToString } from '../utils/apiError';

/**
 * CutListEntryPage
 *
 * Manages the creation, editing, and viewing of Cut Lists for "Shape Stock".
 * This component mirrors the functionality of the CutSheetEntryPage for consistency.
 * It handles the specific attributes of shape stock, such as quantity per length and
 * estimated cutting time. It now includes a tab for a linear cut list optimizer
 * with auto-generation capabilities.
 */

// Helper function from original file
function formatHoursAndMinutes(decimalHours) {
  if (!decimalHours || decimalHours <= 0) {
    return "No real data yet";
  }
  const totalSeconds = Math.round(decimalHours * 3600);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${minutes}m ${seconds}s`;
}

// New Optimizer Component
function LinearCutListOptimizer({ onGenerationComplete }) {
  const [parts, setParts] = useState([]);
  const [allParts, setAllParts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [kerf, setKerf] = useState(0.125);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generation Modal State
  const [showGenModal, setShowGenModal] = useState(false);
  const [genSets, setGenSets] = useState("");
  const [genProductName, setGenProductName] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [genStatus, setGenStatus] = useState(""); // For progress updates

  const API_BASE = config.API_BASE_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const partsRes = await fetch(`${API_BASE}/parts`);
        const allPartsData = await partsRes.json();
        setAllParts(allPartsData.filter(p => p.stock_type === 'Shape Stock'));

        const materialsRes = await fetch(`${API_BASE}/materials`);
        const allMaterialsData = await materialsRes.json();
        setMaterials(allMaterialsData.filter(m => m.material_category === 'Shape Stock'));
      } catch (err) {
        setError("Failed to load parts and materials for optimizer.");
      }
    };
    fetchData();
  }, [API_BASE]);

  const handleAddPart = () => {
    setParts([...parts, { part_id: "", quantity: 1 }]);
  };

  const handlePartChange = (index, field, value) => {
    const newParts = [...parts];
    newParts[index][field] = value;
    setParts(newParts);
  };
  
  const handleRemovePart = (index) => {
    const newParts = parts.filter((_, i) => i !== index);
    setParts(newParts);
  };

  const handleCalculate = async () => {
    if (!selectedMaterial || parts.length === 0 || parts.some(p => !p.part_id || !p.quantity)) {
      alert("Please select a material and add at least one part with a valid quantity.");
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    const payload = {
      parts: parts.map(p => ({ part_id: parseInt(p.part_id, 10), quantity: parseInt(p.quantity, 10) })),
      material_id: parseInt(selectedMaterial, 10),
      kerf: parseFloat(kerf),
    };

    try {
      const res = await fetch(`${API_BASE}/cut-lists/optimize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(detailToString(data.detail) || "Calculation failed");
      }
      
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Memoized calculation to group unique cut patterns from the results
  const summarizedResults = useMemo(() => {
    if (!results || !results.cut_sheets) {
      return null;
    }

    const uniquePatterns = {};
    results.cut_sheets.forEach(sheet => {
      // Create a consistent, sorted key for the pattern to identify duplicates
      const sortedParts = Object.entries(sheet.parts).sort();
      const patternKey = JSON.stringify(sortedParts);

      if (uniquePatterns[patternKey]) {
        uniquePatterns[patternKey].quantity++;
      } else {
        uniquePatterns[patternKey] = {
          pattern: sheet,
          quantity: 1,
        };
      }
    });

    // Convert the map of unique patterns back into an array for rendering
    return Object.values(uniquePatterns);
  }, [results]);

  // --- Auto-Generation Logic ---

  const openGenerationModal = () => {
    setGenSets("");
    setGenProductName("");
    setGenStatus("");
    setShowGenModal(true);
  };

  const handleConfirmGenerate = async () => {
    if (!genSets || !genProductName) {
        alert("Please enter both Number of Sets and Product Name.");
        return;
    }

    setIsGenerating(true);
    setGenStatus("Starting generation...");

    try {
        const totalPatterns = summarizedResults.length;
        
        for (let i = 0; i < totalPatterns; i++) {
            const item = summarizedResults[i];
            const patternIndex = i + 1;
            const requiredSticks = item.quantity;
            
            setGenStatus(`Creating Cut List ${patternIndex} of ${totalPatterns}...`);

            // 1. Construct Name: QTY4-HYFLOSTNDWIDE-1of5-4x
            const cutListName = `QTY${genSets}-${genProductName}-${patternIndex}of${totalPatterns}-${requiredSticks}x`;

            // 2. Construct Description & Find Customer
            // Description: Cut 1x of "PartA" (58.5" each)
            let descriptionLines = [];
            let detectedCustomerId = null;

            // Iterate through parts to build description and find first customer ID
            for (const [partIdStr, count] of Object.entries(item.pattern.parts)) {
                const pId = parseInt(partIdStr);
                const partInfo = allParts.find(p => p.part_id === pId);
                
                if (partInfo) {
                    descriptionLines.push(`Cut ${count}x of "${partInfo.product_id}" (${partInfo.size_length}" each)`);
                    // Grab customer from the FIRST part found that has one
                    if (!detectedCustomerId && partInfo.customer_id) {
                        detectedCustomerId = partInfo.customer_id;
                    }
                } else {
                    descriptionLines.push(`Cut ${count}x of Part #${pId}`);
                }
            }
            const description = descriptionLines.join("\n");

            // 3. Create Cut List Header
            const headerPayload = {
                cut_list_name: cutListName,
                description: description,
                material_id: parseInt(selectedMaterial, 10),
                active_status: true, // Auto-set to Active
                notes: `Auto-generated from Optimizer. Pattern repeats ${requiredSticks} times.`,
                initial_est_cutting_minutes: 0, 
                customer_id: detectedCustomerId
            };

            const headerRes = await fetch(`${API_BASE}/cut_lists`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(headerPayload),
            });

            if (!headerRes.ok) throw new Error(`Failed to create header for ${cutListName}`);
            const headerData = await headerRes.json();
            const newCutListId = headerData.cut_list_id;

            // 4. Add Parts to Cut List
            // We iterate through the parts in this pattern
            for (const [partIdStr, count] of Object.entries(item.pattern.parts)) {
                const partPayload = {
                    part_id: parseInt(partIdStr),
                    quantity_per_length: count // quantity per SINGLE stick
                };

                const partRes = await fetch(`${API_BASE}/cut_lists/${newCutListId}/parts`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(partPayload)
                });
                
                if (!partRes.ok) throw new Error(`Failed to add parts to ${cutListName}`);
            }
        }

        setGenStatus("Complete!");
        setTimeout(() => {
            setShowGenModal(false);
            setIsGenerating(false);
            if (onGenerationComplete) onGenerationComplete(); // Refresh sidebar
            alert("Cut Lists Generated Successfully!");
        }, 500);

    } catch (err) {
        console.error(err);
        setGenStatus(`Error: ${err.message}`);
        setIsGenerating(false);
    }
  };


  return (
    <div>
      <h4 className="mt-3">Linear Cut List Optimizer</h4>
      <p className="text-muted">Calculate the optimal way to cut a list of parts from available stock lengths to minimize waste.</p>

      {/* Inputs */}
      <div className="card p-3 mb-3">
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label fw-bold">1. Select Material Stock</label>
            <select className="form-select" value={selectedMaterial} onChange={e => setSelectedMaterial(e.target.value)}>
              <option value="">-- Select Material --</option>
              {materials.map(m => <option key={m.material_id} value={m.material_id}>{m.material_code} - {m.description} (Length: {m.stock_length}")</option>)}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label fw-bold">2. Set Kerf / Blade Thickness</label>
            <div className="input-group">
                <input type="number" className="form-control" value={kerf} onChange={e => setKerf(e.target.value)} />
                <span className="input-group-text">inches</span>
            </div>
          </div>
        </div>

        <h5 className="mt-2 fw-bold">3. Add Parts and Quantities</h5>
        {parts.map((part, index) => (
          <div key={index} className="row g-2 mb-2 align-items-center">
            <div className="col-md-7">
              <select className="form-select" value={part.part_id} onChange={e => handlePartChange(index, 'part_id', e.target.value)}>
                <option value="">-- Select Part --</option>
                {allParts.map(p => <option key={p.part_id} value={p.part_id}>{p.product_id} - {p.description} (Length: {p.size_length}")</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <input type="number" className="form-control" placeholder="Quantity" value={part.quantity} onChange={e => handlePartChange(index, 'quantity', e.target.value)} min="1" />
            </div>
            <div className="col-md-2">
                <button className="btn btn-sm btn-outline-danger" onClick={() => handleRemovePart(index)}>Remove</button>
            </div>
          </div>
        ))}
        <div className="mt-2">
            <button className="btn btn-sm btn-outline-secondary" onClick={handleAddPart}>+ Add Part</button>
        </div>
      </div>


      <button className="btn btn-primary btn-lg" onClick={handleCalculate} disabled={loading}>
        {loading ? "Calculating..." : "Calculate Optimal Cut List"}
      </button>

      {/* Results */}
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      {summarizedResults && results && (
        <div className="mt-4">
          <hr />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Optimizer Results</h4>
            {/* GENERATE BUTTON */}
            <button className="btn btn-success" onClick={openGenerationModal}>
                Generate Cut Lists from Results
            </button>
          </div>

          <div className="alert alert-success">
            <strong>Summary:</strong> Total Stock Used: <strong>{results.total_stock_used} pieces</strong> | Total Waste: <strong>{results.total_waste.toFixed(2)} inches</strong>
          </div>
          {summarizedResults.map((item, index) => (
            <div key={index} className="card mb-3">
              <div className="card-header bg-light">
                <strong>Pattern #{index + 1}</strong> (Repeats: {item.quantity}x) | Waste: {item.pattern.waste.toFixed(2)}" each
              </div>
              <ul className="list-group list-group-flush">
                {Object.entries(item.pattern.parts).map(([partId, count]) => {
                  const partInfo = allParts.find(p => p.part_id === parseInt(partId));
                  return (
                    <li key={partId} className="list-group-item d-flex justify-content-between">
                        <span>{count}x <strong>{partInfo?.product_id}</strong></span>
                        <span className="text-muted">{partInfo?.size_length}" each</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* GENERATION MODAL */}
      {showGenModal && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Generate Cut Lists</h5>
                        <button type="button" className="btn-close" onClick={() => setShowGenModal(false)} disabled={isGenerating}></button>
                    </div>
                    <div className="modal-body">
                        {isGenerating ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary mb-3" role="status"></div>
                                <p>{genStatus}</p>
                            </div>
                        ) : (
                            <>
                                <p className="small text-muted">
                                    This will automatically create <strong>{summarizedResults.length}</strong> new cut lists based on the optimized patterns.
                                </p>
                                <div className="mb-3">
                                    <label className="form-label">Number of sets</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="e.g. 4" 
                                        value={genSets} 
                                        onChange={e => setGenSets(e.target.value)} 
                                    />
                                    <div className="form-text">Used for naming only (e.g. QTY4...)</div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Name of Product</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="e.g. HYFLOSTNDWIDE" 
                                        value={genProductName} 
                                        onChange={e => setGenProductName(e.target.value)} 
                                    />
                                </div>
                                <div className="alert alert-info small">
                                    <strong>Preview Name:</strong><br/>
                                    {genSets && genProductName ? 
                                        `QTY${genSets}-${genProductName}-1of${summarizedResults.length}-${summarizedResults[0]?.quantity}x` 
                                        : "Enter details above to see preview"}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={() => setShowGenModal(false)} disabled={isGenerating}>Cancel</button>
                        <button className="btn btn-success" onClick={handleConfirmGenerate} disabled={isGenerating}>Generate Cut Lists</button>
                    </div>
                </div>
            </div>
        </div>
      )}

    </div>
  );
}


export default function CutListEntryPage() {
  const { cutListId } = useParams();
  const [activeTab, setActiveTab] = useState("details");

  // Left panel: listing & search
  const [cutLists, setCutLists] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [selectedCutList, setSelectedCutList] = useState(null);

  // Right panel fields
  const [dbCutListId, setDbCutListId] = useState(null);
  const [cutListName, setCutListName] = useState("");
  const [description, setDescription] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [activeStatus, setActiveStatus] = useState(true);
  const [notes, setNotes] = useState("");
  const [dropMaterialId, setDropMaterialId] = useState("");
  const [dropQuantity, setDropQuantity] = useState("");

  // Estimated Time for the primary fabrication process (e.g., Cutting)
  const [initialCuttingMinutes, setInitialCuttingMinutes] = useState("");
  const [historicalAvgHours, setHistoricalAvgHours] = useState(0);

  // Customer ID selector
  const [customerId, setCustomerId] = useState("");

  // Image
  const [imageUrl, setImageUrl] = useState("");
  const [uploadFile, setUploadFile] = useState(null);

  // cut_list_parts array
  const [cutListParts, setCutListParts] = useState([]);

  // Data for dropdowns and part selection
  const [allParts, setAllParts] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);

  // For sub-panel: adding a new part
  const [newPartId, setNewPartId] = useState("");
  const [newPartQty, setNewPartQty] = useState("");

  // UI State
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Flagging fields
  const [flagged, setFlagged] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flaggedByName, setFlaggedByName] = useState("");

  const totalCalculatedCuttingTime = useMemo(() => {
    if (!cutListParts || cutListParts.length === 0) {
      return 0;
    }
    // Note: This assumes the API response for cut list parts will include 'initial_est_cutting_minutes'
    return cutListParts.reduce((total, part) => {
      const partTime = part.initial_est_cutting_minutes || 0;
      const qty = part.quantity_per_length || 0;
      return total + (partTime * qty);
    }, 0);
  }, [cutListParts]);

  const API_BASE = config.API_BASE_URL;

  // Load general data on component mount
  useEffect(() => {
    loadCutLists();
    loadAllParts();
    loadAllMaterials();
    loadAllCustomers();
  }, []);

  // If a cutListId is in the URL, fetch that specific record
  useEffect(() => {
    if (cutListId) {
      loadOneCutList(cutListId);
    }
  }, [cutListId]);

  async function loadOneCutList(id) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/cut_lists/${id}`);
      if (!res.ok) throw new Error(`Failed to fetch cut list #${id}: ${res.status}`);
      const detailData = await res.json();
      detailData.flagged = !!detailData.flagged;
      fillForm(detailData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadCutLists() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/cut_lists`);
      if (!res.ok) throw new Error(`Failed to list cut lists: ${res.status}`);
      const rawData = await res.json();
      const data = rawData.map((cl) => ({ ...cl, flagged: !!cl.flagged }));
      setCutLists(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Helper functions to load dropdown data
  async function loadAllParts() {
    try {
      const res = await fetch(`${API_BASE}/parts`);
      if (!res.ok) throw new Error(`Failed to list parts: ${res.status}`);
      setAllParts(await res.json());
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadAllMaterials() {
    try {
      const res = await fetch(`${API_BASE}/materials`);
      if (!res.ok) throw new Error(`Failed to list materials: ${res.status}`);
      const materials = await res.json();
      setAllMaterials(materials.filter(m => m.material_category === 'Shape Stock'));
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadAllCustomers() {
    try {
      const res = await fetch(`${API_BASE}/customers`);
      if (!res.ok) throw new Error(`Failed to list customers: ${res.status}`);
      setAllCustomers((await res.json()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError(err.message);
    }
  }

  // Filter logic for the left panel
  const filteredCutLists = cutLists.filter((cl) => {
    if (showFlaggedOnly && !cl.flagged) return false;
    const term = searchTerm.toLowerCase();
    if (!((cl.cut_list_name || "").toLowerCase().includes(term) || (cl.description || "").toLowerCase().includes(term))) return false;
    if (productFilter.trim() !== "") {
      const pTerm = productFilter.toLowerCase();
      if (!cl.parts?.some(p => (p.product_id || "").toLowerCase().includes(pTerm))) return false;
    }
    if (customerFilter && cl.customer_id !== parseInt(customerFilter, 10)) return false;
    return true;
  });

  function handleCreateNew() {
    setSelectedCutList(null);
    setDbCutListId(null);
    setCutListName("");
    setDescription("");
    setMaterialId("");
    setActiveStatus(true);
    setNotes("");
    setDropMaterialId("");
    setDropQuantity("");
    setImageUrl("");
    setUploadFile(null);
    setCutListParts([]);
    setInitialCuttingMinutes("");
    setHistoricalAvgHours(0);
    setCustomerId("");
    setFlagged(false);
    setFlagReason("");
    setFlaggedByName("");
    setActiveTab("details"); // Reset to details tab
  }

  async function handleSelectCutList(cl) {
    setSelectedCutList(cl);
    await loadOneCutList(cl.cut_list_id);
    setActiveTab("details"); // Switch to details tab when a list is selected
  }

  function fillForm(clDetail) {
    setDbCutListId(clDetail.cut_list_id);
    setCutListName(clDetail.cut_list_name || "");
    setDescription(clDetail.description || "");
    setMaterialId(clDetail.material_id?.toString() || "");
    setActiveStatus(!!clDetail.active_status);
    setNotes(clDetail.notes || "");
    setDropMaterialId(clDetail.drop_material_id?.toString() || "");
    setDropQuantity(clDetail.drop_quantity?.toString() || "");
    setImageUrl(clDetail.image_path || "");
    setUploadFile(null);
    setInitialCuttingMinutes(clDetail.initial_est_cutting_minutes?.toString() || "");
    setHistoricalAvgHours(clDetail.avg_hrs_per_length || 0);
    setCutListParts(clDetail.parts || []);
    setCustomerId(clDetail.customer_id?.toString() || "");
    setFlagged(!!clDetail.flagged);
    setFlagReason(clDetail.flag_reason || "");
    setFlaggedByName(clDetail.flagged_by_name || "");
  }

  async function handleSaveCutList() {
    setError(null);
    const payload = {
      cut_list_name: cutListName,
      description,
      material_id: materialId ? Number(materialId) : null,
      active_status: activeStatus,
      notes,
      drop_material_id: dropMaterialId ? Number(dropMaterialId) : null,
      drop_quantity: dropQuantity ? Number(dropQuantity) : 0,
      initial_est_cutting_minutes: initialCuttingMinutes ? parseFloat(initialCuttingMinutes) : 0,
      customer_id: customerId ? parseInt(customerId, 10) : null,
    };

    const url = dbCutListId ? `${API_BASE}/cut_lists/${dbCutListId}` : `${API_BASE}/cut_lists`;
    const method = dbCutListId ? "PATCH" : "POST";

    try {
      const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const result = await res.json();
      if (method === "POST") {
        alert(`New cut list created! ID = ${result.cut_list_id}`);
        setDbCutListId(result.cut_list_id);
      } else {
        alert(`Cut list #${dbCutListId} updated.`);
      }
      loadCutLists();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAddPart() {
    if (!dbCutListId) return alert("Save/create this cut list first.");
    if (!newPartId || !newPartQty) return alert("Select a part and enter a quantity per length.");

    const payload = { part_id: Number(newPartId), quantity_per_length: Number(newPartQty) };

    try {
      const res = await fetch(`${API_BASE}/cut_lists/${dbCutListId}/parts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Part added/updated on cut list.");
      await loadOneCutList(dbCutListId);
      setNewPartId("");
      setNewPartQty("");
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleCalculateMaterialCosts() {
    if (!dbCutListId) {
      alert("Please select a saved cut list first.");
      return;
    }

    const confirmCalc = window.confirm(
      "This will recalculate and overwrite the material cost for every part on this list based on the list's assigned material. Continue?"
    );

    if (!confirmCalc) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/cut_lists/${dbCutListId}/calculate_material_costs`,
        {
          method: "POST",
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(detailToString(data.detail) || "Calculation failed");
      }
      alert(data.message);
      // Reload the cut list data to show the new costs
      loadOneCutList(dbCutListId);
    } catch (err) {
      setError(err.message);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeletePart(partId) {
    if (!dbCutListId || !window.confirm("Are you sure you want to remove this part?")) return;
    try {
      const res = await fetch(`${API_BASE}/cut_lists/${dbCutListId}/parts/${partId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      alert("Part removed.");
      await loadOneCutList(dbCutListId);
    } catch (err) {
      setError(err.message);
    }
  }

  function checkAndSetFile(file) {
    const img = new Image();
    img.onload = () => {
      if (img.width > 3000 || img.height > 3000) {
        alert("Image is larger than 3000x3000. Please resize or pick a smaller screenshot.");
        return;
      }
      setUploadFile(file);
    };
    img.src = URL.createObjectURL(file);
  }

  function handlePaste(e) {
    if (e.clipboardData?.files?.length) {
      checkAndSetFile(e.clipboardData.files[0]);
      e.preventDefault();
    }
  }

  function handleFileChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      checkAndSetFile(e.target.files[0]);
    } else {
      setUploadFile(null);
    }
  }

  async function handleUploadImage() {
    if (!dbCutListId) return alert("Create or select a cut list first.");
    if (!uploadFile) return alert("No file selected.");
    const formData = new FormData();
    formData.append("file", uploadFile);
    try {
      const res = await fetch(`${API_BASE}/cut_lists/${dbCutListId}/upload-image`, { method: "POST", body: formData });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json();
      alert("Image uploaded!");
      setImageUrl(data.image_url);
      setUploadFile(null);
    } catch (err) {
      setError(err.message);
    }
  }

  function handleEnlarge(url) { setEnlargedImage(url); }
  function closeEnlarge() { setEnlargedImage(null); }

  async function handleToggleFlag(e) {
    const newValue = e.target.checked;
    if (!dbCutListId) {
      alert("Save or select a cut list first before flagging.");
      e.preventDefault();
      return;
    }

    if (!flagged && newValue) { // Turning ON
      const reason = prompt("Enter flag reason:");
      if (!reason) return e.preventDefault();
      const userName = prompt("Enter your name:", "");
      if (!userName) return e.preventDefault();

      try {
        const payload = { flagged: true, flag_reason: reason, flagged_by_name: userName, flagged_at: new Date().toISOString() };
        const res = await fetch(`${API_BASE}/cut_lists/${dbCutListId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
        alert("Cut list flagged.");
        setFlagged(true);
        setFlagReason(reason);
        setFlaggedByName(userName);
        loadCutLists();
      } catch (err) {
        alert("Error flagging cut list: " + err.message);
        e.preventDefault();
      }
    } else if (flagged && !newValue) { // Turning OFF
      if (!window.confirm("Clear the flag and reset reason?")) return e.preventDefault();
      try {
        const payload = { flagged: false, flag_reason: null, flagged_by_name: null, flagged_at: null };
        const res = await fetch(`${API_BASE}/cut_lists/${dbCutListId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!res.ok) throw new Error(await apiErrorMessage(res));
        alert("Cut list unflagged.");
        setFlagged(false);
        setFlagReason("");
        setFlaggedByName("");
        loadCutLists();
      } catch (err) {
        alert("Error unflagging cut list: " + err.message);
        e.preventDefault();
      }
    }
  }

  return (
    <div className="d-flex" style={{ height: "calc(100vh - 56px)" }}>
      <div className="border-end p-3" style={{ width: "350px", flexShrink: 0, overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Cut Lists (Shape Stock)</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={handleCreateNew}>New</button>
        </div>
        <input type="text" className="form-control mb-2" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <input type="text" className="form-control mb-2" placeholder="Filter by Product ID..." value={productFilter} onChange={(e) => setProductFilter(e.target.value)} />
        <select className="form-select mb-2" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
          <option value="">-- All Customers --</option>
          {allCustomers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
        </select>
        <div className="form-check mb-2">
          <input className="form-check-input" type="checkbox" id="showFlaggedOnly" checked={showFlaggedOnly} onChange={(e) => setShowFlaggedOnly(e.target.checked)} />
          <label className="form-check-label" htmlFor="showFlaggedOnly">Show Flagged Only</label>
        </div>
        {loading ? <div>Loading...</div> : filteredCutLists.map((cl) => (
          <div key={cl.cut_list_id} className={`p-2 border-bottom ${selectedCutList?.cut_list_id === cl.cut_list_id ? "bg-primary-subtle" : "bg-white"}`} style={{ cursor: "pointer" }} onClick={() => handleSelectCutList(cl)}>
            <div className="fw-bold">
              {cl.flagged && <span style={{ color: "red", marginRight: "4px" }}>🚩</span>}
              {cl.cut_list_name || "Unnamed"}
            </div>
            <div className="text-muted" style={{ fontSize: "0.9rem" }}>{cl.description}</div>
          </div>
        ))}
      </div>

      <div className="flex-grow-1 p-4 overflow-auto">
        {error && <div className="alert alert-danger">{error}</div>}
        
        <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Details & Parts</button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'optimizer' ? 'active' : ''}`} onClick={() => setActiveTab('optimizer')}>Cut Optimizer</button>
            </li>
        </ul>

        {activeTab === 'optimizer' && <LinearCutListOptimizer onGenerationComplete={loadCutLists} />}
        
        {activeTab === 'details' && (
            <div>
                <h4>{dbCutListId ? `Editing Cut List` : "Create New Cut List"}</h4>
                <div className="row mb-3">
                <div className="col-md-6"><label className="form-label">Cut List Name</label><input type="text" className="form-control" value={cutListName} onChange={(e) => setCutListName(e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label">Active?</label><div className="form-check form-switch mt-2"><input className="form-check-input" type="checkbox" checked={activeStatus} onChange={(e) => setActiveStatus(e.target.checked)} /><label className="form-check-label">{activeStatus ? "Yes" : "No"}</label></div></div>
                </div>

                <div className="row mb-3">
                <div className="col-md-6"><label className="form-label">Flagged?</label><div className="form-check form-switch mt-2"><input className="form-check-input" type="checkbox" checked={flagged} onChange={handleToggleFlag} /><label className="form-check-label">{flagged ? "Yes" : "No"}</label></div>{flagged && flagReason && (<div className="alert alert-danger mt-2"><strong>Flag Reason:</strong> {flagReason}{flaggedByName && <div><em>Flagged By: {flaggedByName}</em></div>}</div>)}</div>
                </div>
                
                <div className="row mb-3">
                <div className="col-md-6"><label className="form-label">Description</label><input type="text" className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
                <div className="col-md-6"><label className="form-label">Estimated Cutting Time (min)</label><input type="number" className="form-control" min={0} value={initialCuttingMinutes} onChange={(e) => setInitialCuttingMinutes(e.target.value)} />{historicalAvgHours > 0 && <small className="text-muted">Historical Average: {formatHoursAndMinutes(historicalAvgHours)}</small>}</div>
                </div>

                <div className="row mb-3">
                <div className="col-md-6"><label className="form-label">Customer</label><select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}><option value="">-- Select Customer --</option>{allCustomers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}</select></div>
                <div className="col-md-6"><label className="form-label">Shape Stock Material</label><select className="form-select" value={materialId} onChange={(e) => setMaterialId(e.target.value)}><option value="">-- Select Material --</option>{allMaterials.map(m => <option key={m.material_id} value={m.material_id}>{m.material_code} - {m.description}</option>)}</select></div>
                </div>
                
                <div className="row mb-3">
                    <div className="col-md-6"><label className="form-label">Notes</label><textarea className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                </div>

                <button className="btn btn-primary mb-3" onClick={handleSaveCutList}>{dbCutListId ? "Save Changes" : "Create Cut List"}</button>
                <hr />
                
                <div className="mb-3" onPaste={handlePaste}>
                <h5>Cut List Image</h5>
                {imageUrl ? <img src={imageUrl} alt="Cut List" className="img-thumbnail mb-2 dark-invertible" style={{ width: "150px", cursor: "pointer" }} onClick={() => handleEnlarge(imageUrl)} /> : <p>No image for this cut list yet.</p>}
                <div className="input-group mb-3" style={{ maxWidth: "400px" }}><input type="file" className="form-control" accept="image/*" onChange={handleFileChange} /><button className="btn btn-secondary" onClick={handleUploadImage}>Upload Image</button></div>
                {uploadFile && <div style={{ marginTop: "1rem" }}><p>Preview (unsaved):</p><img src={URL.createObjectURL(uploadFile)} alt="Preview" style={{ maxWidth: "400px", border: "1px solid #ccc" }} /></div>}
                <small className="text-muted">You can also paste an image (Ctrl+V) directly into this area.</small>
                </div>
                <hr />

                {dbCutListId && (
                    <div>
                        <h5>Parts in This Cut List</h5>
                        <table className="table table-sm table-bordered mb-3">
                        <thead>
                            <tr>
                            <th>Part ID</th>
                            <th>Product ID</th>
                            <th>Description</th>
                            <th>Qty/Length</th>
                            <th>Est. Cutting (min)</th>
                            <th>Nested Material Cost</th>
                            <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cutListParts.length === 0 ? (
                            <tr>
                                <td colSpan="7">No parts in this cut list.</td>
                            </tr>
                            ) : (
                            cutListParts.map(p => (
                                <tr key={p.part_id}>
                                <td>{p.part_id}</td>
                                <td>{p.product_id}</td>
                                <td>{p.part_description}</td>
                                <td>{p.quantity_per_length}</td>
                                <td>{p.initial_est_cutting_minutes || 0}</td>
                                <td>
                                    {p.material_cost
                                    ? `$${Number(p.material_cost).toFixed(2)}`
                                    : "N/A"}
                                </td>
                                <td>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleDeletePart(p.part_id)}>
                                    Delete
                                    </button>
                                </td>
                                </tr>
                            ))
                            )}
                        </tbody>
                        <tfoot>
                        <tr>
                            <td colSpan="7" className="text-end">
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={handleCalculateMaterialCosts}
                                disabled={!materialId || cutListParts.length === 0}
                                title={!materialId ? "Assign a material to the cut list first" : "Calculate material costs"}
                            >
                                Calculate Material Prices
                            </button>
                            </td>
                        </tr>
                        </tfoot>
                        </table>
                        {totalCalculatedCuttingTime > 0 && <div className="alert alert-info" style={{ maxWidth: "500px" }}><strong>Calculated Cutting Time:</strong> {totalCalculatedCuttingTime} minutes.<br /><small>Sum of each part's est. time multiplied by its quantity.</small></div>}
                        <div className="row g-2 align-items-end" style={{ maxWidth: "500px" }}>
                            <div className="col-md-5">
                            <label className="form-label">Part</label>
                            <select
                                className="form-select"
                                value={newPartId}
                                onChange={(e) => setNewPartId(e.target.value)}
                            >
                                <option value="">-- Select Part --</option>
                                {allParts
                                .filter(p => p.stock_type === 'Shape Stock') 
                                .map(p => (
                                    <option key={p.part_id} value={p.part_id}>
                                    {p.product_id} - {p.description}
                                    </option>
                                ))}
                            </select>
                            </div>
                            <div className="col-md-3"><label className="form-label">Qty/Length</label><input type="number" className="form-control" value={newPartQty} onChange={(e) => setNewPartQty(e.target.value)} /></div>
                            <div className="col-md-4"><button className="btn btn-success w-100" onClick={handleAddPart}>Add/Update Part</button></div>
                        </div>
                    </div>
                )}
            </div>
        )}
      </div>

      {enlargedImage && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={closeEnlarge}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header"><button type="button" className="btn-close" onClick={closeEnlarge}></button></div>
              <div className="modal-body text-center"><img src={enlargedImage} alt="Enlarged" style={{ maxWidth: "100%", maxHeight: "80vh" }} /></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}